import { nonEmptyString } from "./json-body";
import type { PromptPart } from "./transport";

export const ENCRYPTED_REASONING_LABEL = "Encrypted reasoning, no summary";

/**
 * Reasoning the model sent back only encrypted. The request carries it as ciphertext the provider
 * decrypts, so its tokens are the reasoning tokens of the call that produced it, not its length.
 */
export const encryptedReasoningPart = (ciphertext: string): PromptPart => ({
  role: "assistant",
  label: ENCRYPTED_REASONING_LABEL,
  text: "",
  sent: ciphertext,
});

/** One tool definition as a prompt part: its name as the label, the rest as JSON. */
export function toolPart(definition: Record<string, unknown>): PromptPart {
  const name = nonEmptyString(definition.name) ?? nonEmptyString(definition.type) ?? "tool";
  return {
    role: "tools",
    label: `Tool ${name}`,
    text: JSON.stringify(
      Object.fromEntries(Object.entries(definition).filter(([key]) => key !== "name")),
      null,
      2,
    ),
    sent: JSON.stringify(definition),
  };
}

/**
 * How much of `current` repeats `previous` from the start: every whole part that matches, then
 * the characters the first differing part shares. Providers cache a repeated prefix.
 */
export function sharedPromptPrefix(
  previous: readonly PromptPart[],
  current: readonly PromptPart[],
): { wholeParts: number; characters: number } {
  let wholeParts = 0;
  while (
    wholeParts < previous.length &&
    wholeParts < current.length &&
    samePart(previous[wholeParts], current[wholeParts])
  )
    wholeParts += 1;
  const before = previous[wholeParts];
  const after = current[wholeParts];
  let characters = 0;
  if (before && after && before.role === after.role && before.label === after.label) {
    const limit = Math.min(before.text.length, after.text.length);
    while (characters < limit && before.text[characters] === after.text[characters])
      characters += 1;
  }
  return { wholeParts, characters };
}

const samePart = (left: PromptPart | undefined, right: PromptPart | undefined) =>
  !!left &&
  !!right &&
  left.role === right.role &&
  left.label === right.label &&
  left.text === right.text;
